import { Calculation } from '@carbon/react/icons';
import type { NriMatrixFormulaInputs } from 'types/api-responses';
import { BaseCard } from 'components/base-card';
import { CalculationIconTrigger } from 'components/calculation-icon-trigger';
import { UATCalculationPopup } from 'components/nri-table/uat-nri-calculation-popup';
import { CalculationType } from 'components/nri-table/types';
import {
  AllocationMethod,
  AllocationSource,
  toNriCalculationMethod,
  getUatValues,
  buildTractNriFormula,
} from './tract-nri-matrix-utils';

export interface UatCalculationContentProps {
  interestValue: number;
  formulaInputs: NriMatrixFormulaInputs | null;
  allocationMethod: AllocationMethod;
  source: AllocationSource;
  rowValue: number | null;
  calculationType: CalculationType;
}

/**
 * The calculation behind one NRI Matrix row (allocation method + source) —
 * the content of that row's "Show Calculation" popover.
 *
 * @param {UatCalculationContentProps} props - The row and the matrix inputs.
 * @return {JSX.Element} The calculation popup.
 */
export function UatCalculationContent({
  interestValue,
  formulaInputs,
  allocationMethod,
  source,
  rowValue,
  calculationType,
}: UatCalculationContentProps) {
  const isLateral = allocationMethod === 'lateralLength';
  const hasAllocationManager = formulaInputs?.allocationManagerPresent ?? false;
  const participationTractGisAc = formulaInputs?.participationTractGisAc ?? 0;
  const tractValue =
    calculationType === 'WI'
      ? (formulaInputs?.tractWi ?? 0)
      : (formulaInputs?.tractNri ?? 0);

  const { uatValue, unitValue, participationDenominatorAc } = getUatValues(
    formulaInputs,
    rowValue,
    allocationMethod,
    source,
    hasAllocationManager
  );

  return (
    <UATCalculationPopup
      tractValue={tractValue}
      uat={uatValue}
      uatUnit={isLateral ? 'ft' : 'ac'}
      length={unitValue}
      lengthUnit={isLateral ? 'ft' : 'ac'}
      participationTract={participationTractGisAc}
      participationTractUnit="ac"
      uatValueAc={participationDenominatorAc}
      uatValueAcUnit="ac"
      result={interestValue}
      calculationType={calculationType}
      hasAllocationManager={hasAllocationManager}
      tractNriFormula={buildTractNriFormula(formulaInputs)}
      tractNriCalculationMethod={toNriCalculationMethod(
        formulaInputs?.netAcreageUnits
      )}
      instrumentType={formulaInputs?.instrumentType}
    />
  );
}

export interface UatInterestCellProps extends Omit<
  UatCalculationContentProps,
  'interestValue'
> {
  interestValue: number | null;
  displayValue: React.ReactNode;
  cellClassName: string;
  cardContentClassName: string;
  iconContainerClassName: string;
}

export const UatInterestCell = ({
  interestValue,
  displayValue,
  cellClassName,
  cardContentClassName,
  iconContainerClassName,
  ...calculation
}: UatInterestCellProps) => {
  if (interestValue === null) {
    return <span>--</span>;
  }

  const renderTrigger = (active: boolean, toggle: () => void) => (
    <CalculationIconTrigger
      definition="Show Calculation"
      active={active}
      onClick={toggle}
    >
      <div className={iconContainerClassName}>
        <Calculation aria-hidden="true" role="img" />
      </div>
    </CalculationIconTrigger>
  );

  return (
    <div className={cellClassName}>
      <span>{displayValue}</span>
      <BaseCard
        contentClassName={cardContentClassName}
        renderTrigger={renderTrigger}
        content={
          <UatCalculationContent
            interestValue={interestValue}
            {...calculation}
          />
        }
      />
    </div>
  );
};
